import React, { useEffect, useState } from 'react'
import { useHistory } from "react-router-dom";
import { getUnixTime, fromUnixTime, sub, isSameDay, isSameMonth, isSameYear } from 'date-fns'
import { isEmpty, keys } from 'ramda'
import { getData } from '../../../helpers'
import { useFetch, useForm } from '../../../hooks'
import FromRadio from '../../../components/inputs/FromRadio'
import DatePicker from '../../../components/DatePicker'
import Checkbox from '../../../components/inputs/Checkbox'
import Buttons from '../../../modules/Buttons'
import Button from '../../../components/inputs/Button'
import { StyledForm, StyledFormHeader, StyledPeriod, StyledPeriodTitle, StyledDateRange, StyledDateRangeItem, StyledReports, StyledReportsTitle, StyledCheckboxWrapper } from './styled'

const FormGenerateReport = ({ type, dataKey, icon, id, report, endpointExecute, isHistoryReplace, initialData, onClickCancel }) => {
    const history = useHistory();

    const { response: responseModalData, error: errorModalData, doFetch: doFetchModalData } = useFetch({
        method: 'GET',
        lazy: true
    })

    useEffect(() => {
        if (responseModalData.data && report) {
            doFetchModalData({
                url: `/${report.endpoint}`
            })
        } else {
            onClickCancel()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [report])

    useEffect(() => {
        if (errorModalData && errorModalData.status !== 200) {
            onClickCancel()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [errorModalData])

    const formData = getData(responseModalData)

    const redirectToReports = values => {
        const to = {
            pathname: `/reports/${type}`,
            state: {
                type,
                dataKey,
                icon,
                data: {
                    [`${type}id`]: id,
                    lastweek: last === 'lastweek',
                    lastmonth: last === 'lastmonth',
                    dates: `{${getUnixTime(startDate)}, ${getUnixTime(endDate)}}`,
                    reports: `{${keys(values).reduce((acc, key) => values[key] ? [...acc, parseInt(key, 10)] : acc, []).join(",")}}`
                },
                endpointExecute,
                refetch: isHistoryReplace,
                report
            }
        }
        if (isHistoryReplace) {
            history.replace(to);
        } else {
            history.push(to);
        }
    }

    const setInitialValues = () => {
        if (!isEmpty(formData)) {
            return formData.reports.data.slice().sort((a, b) => b.order - a.order).reduce((values, item) => {
                if (item.label !== null) {
                    return {
                        ...values,
                        [item.key]: !!initialData.reports.find(reportKey => parseInt(reportKey, 10) === parseInt(item.key, 10))
                    }
                }
                return values
            }, {})
        }
        return {}
    }

    const { values, handleChange, handleSubmit } = useForm({
        callback: redirectToReports,
        initValues: setInitialValues()
    })

    const [last, setLast] = useState(null)
    const [activeDatePicker, setActiveDatePicker] = useState(true)
    const [startDate, setStartDate] = useState(new Date())
    const [endDate, setEndDate] = useState(new Date())

    useEffect(() => {
        if (initialData) {
            const _startDate = fromUnixTime(initialData.startDate);
            const _endDate = fromUnixTime(initialData.endDate);
            if (initialData.last !== last) {
                setLast(initialData.last);
            }
            if (!isSameDay(_startDate, startDate) || !isSameMonth(_startDate, startDate) || !isSameYear(_startDate, startDate)) {
                setStartDate(_startDate);
            }
            if (!isSameDay(_endDate, endDate) || !isSameMonth(_endDate, endDate) || !isSameYear(_endDate, endDate)) {
                setEndDate(_endDate);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialData])

    useEffect(() => {
        if (last !== null && activeDatePicker) {
            setActiveDatePicker(false)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [last])

    const handleClickDatePicker = () => {
        setActiveDatePicker(true)
        setLast(null)
    }

    const handleChangeLast = event => {
        event.persist();
        setLast(event.target.value);
    }

    return (
        <>
            {
                !isEmpty(formData) &&
                <StyledForm onSubmit={handleSubmit}>
                    <StyledFormHeader>
                        {formData.title}
                    </StyledFormHeader>
                    <StyledPeriod>
                        <StyledPeriodTitle>
                            {formData.period.title}
                        </StyledPeriodTitle>
                        <FromRadio
                            name='last'
                            onChange={handleChangeLast}
                            value={last}
                            options={[
                                { value: 'lastweek', label: formData.period['label-week'] },
                                { value: 'lastmonth', label: formData.period['label-month'] }
                            ]}
                        />
                        <StyledDateRange>
                            <StyledDateRangeItem
                                onClick={handleClickDatePicker}
                            >
                                <DatePicker
                                    label={formData.period['label-from']}
                                    selected={startDate}
                                    onChange={date => setStartDate(date)}
                                    dateFormat="dd.MM.yyyy"
                                    selectsStart
                                    startDate={startDate}
                                    endDate={endDate}
                                    maxDate={endDate}
                                    active={activeDatePicker}
                                    showPopperArrow={false}
                                />
                            </StyledDateRangeItem>
                            <StyledDateRangeItem
                                onClick={handleClickDatePicker}
                            >
                                <DatePicker
                                    label={formData.period['label-to']}
                                    selected={endDate}
                                    onChange={date => setEndDate(date)}
                                    dateFormat="dd.MM.yyyy"
                                    selectsEnd
                                    endDate={endDate}
                                    minDate={startDate}
                                    maxDate={new Date()}
                                    active={activeDatePicker}
                                    popperPlacement="bottom-end"
                                    showPopperArrow={false}
                                />
                            </StyledDateRangeItem>
                        </StyledDateRange>
                    </StyledPeriod>
                    <StyledReports>
                        <StyledReportsTitle>
                            {formData.reports.title}
                        </StyledReportsTitle>
                        {
                            !isEmpty(values) &&
                            formData.reports.data.slice().sort((a, b) => a.order - b.order).map(item => (
                                <React.Fragment
                                    key={item.order}
                                >
                                    {
                                        item.label &&
                                        <StyledCheckboxWrapper>
                                            <Checkbox name={item.key} checked={values[item.key]} label={item.label} onClick={handleChange} />
                                        </StyledCheckboxWrapper>
                                    }
                                </React.Fragment>
                            ))
                        }
                    </StyledReports>
                    <Buttons>
                        <Button
                            type='button'
                            fullWidth
                            onClick={onClickCancel}
                            width='50%'
                        >
                            {formData.buttons.cancel}
                        </Button>
                        <Button
                            type='submit'
                            fullWidth
                            green
                            width='50%'
                            disabled={!keys(values).find(key => values[key])}
                        >
                            {formData.buttons.save}
                        </Button>
                    </Buttons>
                </StyledForm>
            }
        </>
    )
}

FormGenerateReport.defaultProps = {
    initialData: {
        startDate: getUnixTime(sub(new Date(), { days: 1 })),
        endDate: getUnixTime(new Date()),
        reports: [],
        last: null
    }
}

export default FormGenerateReport
